import SalesforceCampaignMapping from './SalesforceCampaignMapping'
import SalesforceActivityDataMapping from './SalesforceActivityDataMapping'
import SalesforceEventDataMapping from './SalesforceEventDataMapping'
import SalesforceTaskTypeSelect from './SalesforceTaskTypeSelect'
import MinimumCqlScoreWidget from './../MinimumCqlScoreWidget'
import IntegrationRetryDelay from './../IntegrationRetryDelay'
import * as GateSelectors from 'app/modules/gates/selectors'
import ACCESS from 'utils/Access'
import { withProps } from 'recompose'
import { setMinimumCqlScore, getMinimumCqlScore, getIntegrationRetryDelay, setIntegrationRetryDelay } from 'app/modules/integrations/api'

export const OWNER_TOGGLES = [
  {
    type: 'routeContactToSalesforceAccountOwner',
    value: true,
    label: 'Find the Contact by email, use their Account’s owner'
  },
  {
    type: 'routeContactToSalesforceContactOwner',
    value: true,
    label: 'Find the Contact by email, use the Contact owner'
  },
  {
    type: 'routeContactToSalesforceAccountOwnerByDomain',
    value: false,
    label: 'Find the Account whose website matches the email domain, use that Account’s owner'
  },
  {
    type: 'routeLeadToSalesforceLeadOwner',
    value: true,
    label: 'Find the Lead by email, use the Lead owner'
  }
]

export const model = [
  {
    type: 'shouldOnlySyncQualifiedLeadsAndContactsToSalesforce',
    value: false,
    label: 'Set a minimum CQL score for synced contacts and conversations',
    description: 'Unchecked, Drift will sync all contacts and conversations to Salesforce except those that have been disqualified.',
    children: {
      autoExpand: true,
      label: 'Minimum CQL score',
      description: 'Drift will only sync conversations that have the selected CQL score or higher.',
      topLevelAdvancedItems: [],
      advancedComponent: withProps({
        integrationName: 'salesforce',
        setMinimumCQLScore: (data) => {
          return setMinimumCqlScore(data)
        },
        getMinimumCQLScore: (data) => {
          return getMinimumCqlScore(data)
        }
      })(MinimumCqlScoreWidget),
      items: []
    }
  },
  {
    type: 'hasLeadCreationSynced',
    value: false,
    label: 'Sync leads from Drift to Salesforce',
    description: 'New Leads from Drift will be created in Salesforce as a lead record.',
    children: {
      label: 'Set Lead Owner',
      description: 'For every new Lead that Drift creates, we can set a Lead Owner based on the following conversation events in this order. If none are checked, we’ll leave the Lead Owner field blank.',
      topLevelAdvancedItems: [],
      advancedComponent: withProps({ type: 'leads' })(SalesforceCampaignMapping),
      items: [
        {
          type: 'leadOwnerMeetingEnabled',
          label: 'Teammate with a scheduled meeting (if meeting scheduled)',
          value: false
        },
        {
          type: 'leadOwnerRoutingEnabled',
          label: 'User who was last routed to (if routing occurred)',
          value: false
        },
        {
          type: 'leadOwnerConversationClosedEnabled',
          label: 'User who closed conversation',
          value: false
        }
      ]
    }
  },
  {
    type: 'hasContactCreationSynced',
    value: false,
    label: 'Sync contacts from Drift to Salesforce',
    description: 'New Contacts from Drift will be created in Salesforce as a contact record if we can find a suitable account to associate it with.',
    children: {
      label: 'Set Contact Owner',
      description: 'For every new Contact that Drift creates, we can set a Contact Owner based on the following conversation events in this order. If none are checked, we’ll leave the Contact Owner field blank.',
      topLevelAdvancedItems: [
        {
          type: 'shouldAlwaysCreateContactRecord',
          value: false,
          label: 'Create new contacts without accounts',
          description: 'Create a new contact even if we can\'t find a suitable account to associate it with.',
        }
      ],
      advancedComponent: withProps({ type: 'contacts' })(SalesforceCampaignMapping),
      items: [
        {
          type: 'leadOwnerMeetingEnabled',
          label: 'Teammate with a scheduled meeting (if meeting scheduled)',
          value: false
        },
        {
          type: 'leadOwnerRoutingEnabled',
          label: 'User who was last routed to (if routing occurred)',
          value: false
        },
        {
          type: 'leadOwnerConversationClosedEnabled',
          label: 'User who closed conversation',
          value: false
        }
      ]
    }
  },
  {
    type: 'hasAnyActivitySyncEnabled',
    value: false,
    label: 'Send Drift chat activity data to Salesforce',
    description: 'When a lead or contact has a conversation with you in Drift, we’ll add activity to their Salesforce record with the full chat transcript attached when their conversation is closed.',
    children: {
      label: 'Set Activity Owner',
      description: 'For every new conversation in Drift, we create an activity in Salesforce with an Activity Owner based on the following conversation events in this order. If none are checked, we’ll leave the Activity Owner field blank. ',
      topLevelAdvancedItems: [],
      advancedComponent: SalesforceActivityDataMapping,
      items: [
        {
          type: 'activityOwnerMeetingEnabled',
          value: false,
          label: 'User who scheduled a meeting (if a meeting was scheduled)'
        },
        {
          type: 'activityOwnerRoutingEnabled',
          value: false,
          label: 'User who was last routed to (if routing occurred)'
        },
        {
          type: 'activityOwnerConversationClosedEnabled',
          value: false,
          label: 'User who closed the conversation'
        }
      ]
    }
  },
  {
    type: 'meetingBookedInChatBecomesEventEnabled',
    value: false,
    label: 'Send meetings booked in Drift to Salesforce',
    description: 'When a lead or contact books a meeting with you in Drift, we’ll add an event to their Salesforce record',
    children: {
      label: 'Set Event Type',
      description: 'When Drift sends a meeting to Salesforce, we’ll set the event type to what you select below. These are pulled from your configured event fields in Salesforce.',
      topLevelAdvancedItems: [{
        type: 'holdMeetingTillConvoClosed',
        label: 'Only sync meetings when a conversation closes.',
        description:'Meetings will be sent to Salesforce when the conversation closes instead of the moment they\'re booked',
        value: false
      }],
      advancedComponent: SalesforceEventDataMapping,
      items: [

      ]
    }
  },
  {
    type: 'hasPhoneSyncEnabled',
    value: false,
    label: 'Send Drift call activity to Salesforce',
    description: 'When you make a call in Drift, we’ll add a task to Salesforce once the call ends.',
    children: {
      label: 'Set Task Type',
      description: 'When Drift sends call activity to Salesforce, we’ll set the task type to what you select below. These are pulled from your configured task fields in Salesforce.',
      advancedComponent: SalesforceTaskTypeSelect,
      items: [

      ]
    }
  },
  {	
    type: 'salesforceRetryDelayEnabled',	
    value: true,	
    label: `Retry attachment for events and tasks after a delay`,	
    description: `If we fail to attach a meeting event or conversation task to a contact or lead, we'll try again after this delay`,	
    children: {
      label: 'Set retry delay',	
      description: 'Enter the amount of minutes that you want us to wait until we attempt to retry our event/task attachment',	
      topLevelAdvancedItems: [],	
      advancedComponent: withProps({	
          integration:"salesforce", 	
          getDelay: getIntegrationRetryDelay,	
          setDelay: setIntegrationRetryDelay })(IntegrationRetryDelay),	
      items: [	

      ]
    }
  },
  {
    type: 'shouldAlwaysCreateNewRecord',
    value: false,
    label: 'Always create new Salesforce records',
    description: 'Don\'t check for existing leads/contacts when creating conversation and/or meeting activities; instead create a new record every time',
    confirmation: 'This setting will cause Drift to create duplicate records in Salesforce and is only recommended if you have a process in place to handle these duplicates.'
  }
]


export const hydrateModel = toggles => {
  const phoneSyncSettingId = 'hasPhoneSyncEnabled'
  let modelCopy = [...model]

  if (!ACCESS.hasGates('has_drift_voice')) { 
    modelCopy = modelCopy.filter(toggle => toggle.type !== phoneSyncSettingId)
  }

  return modelCopy.map((topLevel) => {
    const updatedToggle = Object.assign({}, topLevel)
    
    updatedToggle.value = toggles[topLevel.type]
    if (!!topLevel.children && topLevel.children.items.length > 0) {
      updatedToggle.children.items = addValuesToToggles(topLevel.children.items, toggles)
    }
    if (hasTopLevelAdvancedItems(topLevel)) {
      updatedToggle.children.topLevelAdvancedItems = addValuesToToggles(topLevel.children.topLevelAdvancedItems, toggles)
    }

    return updatedToggle
  })
}

const hasTopLevelAdvancedItems = (toggle) => {
  return !!toggle.children && !!toggle.children.topLevelAdvancedItems && toggle.children.topLevelAdvancedItems.length > 0
}

const addValuesToToggles = (toggles, fullToggles) => {
  return toggles.map(secondLevel => {
    const updatedSecondaryToggle = Object.assign({}, secondLevel)
    updatedSecondaryToggle.value = !!fullToggles[secondLevel.type]
    return updatedSecondaryToggle
  })
}

export const hydrateOwnerToggles = toggles => {
  return OWNER_TOGGLES.map(toggle => {
    const newToggle = Object.assign({}, toggle)
    newToggle.value = toggles[toggle.type]
    return newToggle
  })
}



// WEBPACK FOOTER //
// ./src/scripts/components/Integration/pages/SalesforceConfig/model.js